import { Injectable } from '@angular/core';
import { CommsService } from '../../utils/comms/comms.service';
import { AuthService } from '../auth/auth.service';
import { IBumpScreenContext, IBumpScreenLocation } from 'src/app/models/bump-screen.model';
import { CoreService } from '../core/core.service';
import { WatcherService } from '../../utils/watcher/watcher.service';
import { NEW_OPENING_HOURS } from '../../utils/watcher/watcher-event';
import { Order } from 'src/app/models/order.model';

import * as moment from 'moment-timezone';

import each from 'lodash/each';
import size from 'lodash/size';
import has from 'lodash/has';
import find from 'lodash/find';

export interface IOpeningStatus {
  timeperiod: string;
  days: IOpeningDay[];
}

export interface IOpeningDay {
  hoursFormatted: string;
  day: string;
  timeslots: IOpeningSlot[];
}

export interface IOpeningSlot {
  open: moment.Moment;
  close: moment.Moment;
}

@Injectable({
  providedIn: 'root',
})
export class OpeningHoursService {
  public openData: {
    [locationID: string]: {
      [contextID: string]: IOpeningStatus;
    };
  } = {};

  constructor(
    public comms: CommsService,
    public auth: AuthService,
    public core: CoreService,
    public watcher: WatcherService,
  ) {}

  public getOpeningStatus(numOfDays: number = 7): void {
    // initialise the opening hours object
    each(this.auth.Active_bump_screen.location_details, (location: IBumpScreenLocation) => {
      if (!this.openData[location.id]) {
        this.openData[location.id] = {};
      }

      each(location.contexts, (context: IBumpScreenContext) => {
        const numDays: number = this.auth.Active_bump_screen.getPreorderDaysByFulfillment(context.context_type);
        if (context.timeperiod) {
          const apiURL: string =
            this.core.Active_server.url +
            'api/online/store/calendar/' +
            context.timeperiod +
            '/' +
            numDays +
            '/' +
            location.id;

          this.comms.get(
            apiURL,
            {},
            val => {
              if (val && val.time_period_id) {
                this._processResponse(location.id, context.id, context.timeperiod, val);
              } else {
                console.log(`FAILED - LOCATION ID: ${location.id}, CONTEXT: ${context.context_type}`, val);
              }
            },
            err => {
              console.log(`FAILED - LOCATION ID: ${location.id}, CONTEXT: ${context.context_type}`, err);
            },
          );
        }
      });
    });
  }

  private _processResponse(locationID: string, contextID: string, timeperiod: string, data: any): void {
    if (data && size(data.days) && !data.closed && data.hoursFormatted != 'Closed') {
      // initialise the object
      this.openData[locationID][contextID] = {
        timeperiod: timeperiod,
        days: [],
      };

      // create the day slots
      each(data.days, (val, key) => {
        if (size(val.hours)) {
          let openHour: IOpeningSlot = { open: null, close: null };
          let newDay: IOpeningDay = {
            day: key,
            hoursFormatted: val.hoursFormatted,
            timeslots: [],
          };

          // create the hour slots
          each(val.hours, node => {
            if (node) {
              let time = moment(node.time, 'HHmm');
              if (node.type == 'Opens' && !openHour.open) {
                openHour.open = time;
              } else if (node.type == 'Closes' && !openHour.close) {
                openHour.close = time;
              }

              if (openHour.open && openHour.close) {
                newDay.timeslots.push(openHour);

                // make a new entry
                openHour = { open: null, close: null };
              }
            }
          });

          // add the created day to the array
          this.openData[locationID][contextID].days.push(newDay);
        }
      });

      this.watcher.broadcast(NEW_OPENING_HOURS);
    }
  }

  public getTargetDayByOrder(order: Order): IOpeningDay {
    let retVal: IOpeningDay;

    if (order && order.location_id && size(this.openData)) {
      // location open data is loaded
      const location: IBumpScreenLocation = find(this.auth.Active_bump_screen.location_details, {
        id: order.location_id,
      });
      if (location && has(this.openData, location.id)) {
        // context opening hours is available
        const context: IBumpScreenContext = find(location.contexts, { context_type: order.context_type });
        if (context && has(this.openData[location.id], context.id)) {
          const openDataToUse: any = this.openData[location.id][context.id];

          if (size(openDataToUse.days)) {
            const dayRef: string = moment(order.requested_time).format('YYYY-MM-DD');
            const dayTarget: IOpeningDay = find(openDataToUse.days, { day: dayRef });
            if (dayTarget && size(dayTarget.timeslots)) {
              retVal = dayTarget;
            } else {
              console.log('NO TARGET DATE', order, dayRef, openDataToUse);
            }
          }
        }
      }
    }

    return retVal;
  }

  public reset(): void {
    this.openData = {};
  }
}
